/**
 * PIQ 9.0: Narrative Spine (Decision Compression)
 * ================================================
 * SINGLE DECISION STACK - exactly ONE of:
 * - ACT: VerdictBar → Top 1–3 Actions → Why
 * - PREPARE: VerdictBar → One Playbook → Why  
 * - HOLD: VerdictBar → HOLD Explanation → Watchlist Chips
 * - CLEAR: VerdictBar → "All clear" explanation
 * 
 * NO in-page Insights Feed. All deep dive via InsightDrawer.
 * 
 * INVARIANT: Only one decision stack renders per week.
 */

import React, { useState, useEffect } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { VerdictBar } from './VerdictBar';
import { ExecBriefPrimaryCard, type ExecBriefData } from './ExecBriefPrimaryCard';
import { DecisionSection, type DecisionStack } from './DecisionSection';
import { WatchlistChips } from './WatchlistChips';
import { InsightDrawer } from './InsightDrawer';
import { buildOperatorView, type BrainContractSubset, type ActionItem, type InsightFeedItem } from './operatorViewAdapter';
import type { InsightDetailVM } from './InsightDetailDrawer';

// ============================================================
// Types
// ============================================================

interface NarrativeSpineProps {
  brainContract: BrainContractSubset;
  execBriefData: ExecBriefData | null;
  isLoading?: boolean;
  isExecBriefLoading?: boolean;
  unifiedActions?: any[];
  suppression?: any;
  onActionClick?: (item: ActionItem) => void;
  onConvertPlaybook?: (id: string) => void;
  onGenerateBrief?: () => void;
}

// ============================================================
// Component
// ============================================================

export function NarrativeSpine({
  brainContract,
  execBriefData,
  isLoading = false,
  isExecBriefLoading = false,
  unifiedActions = [],
  suppression,
  onActionClick,
  onConvertPlaybook,
  onGenerateBrief,
}: NarrativeSpineProps) {
  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'summary' | 'insights' | 'evidence' | 'source'>('summary');
  const [filteredChannel, setFilteredChannel] = useState<string | undefined>();

  // Build operator view (pure mapping from BrainContract)
  const operatorView = buildOperatorView({
    brainContract,
    unifiedActions,
    suppression,
  });

  const posture = brainContract.decisionPosture?.posture || 'monitor_only';
  
  // Get containment/preventive playbooks from brain contract
  const containmentPlaybook = brainContract.containmentPlaybook;
  const preventivePlaybook = brainContract.preventivePlaybooks?.[0];
  
  // PIQ 9.0: Determine the SINGLE decision stack
  // INVARIANT: Exactly ONE of ACT/PREPARE/HOLD/CLEAR
  const actionableItems = operatorView.actSection.items.filter(a => a.roiScore >= 40);
  
  const decisionStack: DecisionStack = (() => {
    // ACT: action_required posture AND actionable items exist
    if (posture === 'action_required' && actionableItems.length > 0) {
      return 'act';
    }
    // PREPARE: containment or preventive playbook exists (regardless of posture)
    if (containmentPlaybook || preventivePlaybook) {
      return 'prepare';
    }
    // CLEAR: all_clear posture
    if (posture === 'all_clear') {
      return 'clear';
    }
    // HOLD: default (monitor_only or action_required with no actionable items)
    return 'hold';
  })();
  
  // PIQ 9.0: Runtime invariant - verify exactly one stack
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const stacks = [
        decisionStack === 'act',
        decisionStack === 'prepare',
        decisionStack === 'hold',
        decisionStack === 'clear',
      ].filter(Boolean);
      
      if (stacks.length !== 1) {
        console.error('[NarrativeSpine] INVARIANT VIOLATED: Expected exactly 1 decision stack, got', stacks.length);
      }
    }
  }, [decisionStack]);
  
  // Show Watchlist only for HOLD/CLEAR
  const showWatchlist = decisionStack === 'hold' || decisionStack === 'clear';
  
  // Total insights count
  const totalInsights = 
    operatorView.insightsFeed.patterns.length +
    operatorView.insightsFeed.opportunities.length +
    operatorView.insightsFeed.marketing.length +
    operatorView.insightsFeed.dataHealth.length +
    operatorView.insightsFeed.learning.length;

  // Open drawer handlers
  const handleWhyClick = () => {
    setDrawerTab('summary');
    setFilteredChannel(undefined);
    setIsDrawerOpen(true);
  };

  const handleViewAllInsights = () => {
    setDrawerTab('insights');
    setFilteredChannel(undefined);
    setIsDrawerOpen(true);
  };

  const handleChannelClick = (channel: string) => {
    setDrawerTab('insights');
    setFilteredChannel(channel);
    setIsDrawerOpen(true);
  };

  const handleChipClick = (item: InsightFeedItem) => {
    setDrawerTab('insights');
    setFilteredChannel(undefined);
    setIsDrawerOpen(true);
  };

  const handleMetricClick = (detail: InsightDetailVM) => {
    // Extract channel from detail and open drawer filtered
    const channel = detail.title || detail.context?.channel;
    if (channel) {
      handleChannelClick(channel);
    } else {
      handleViewAllInsights();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {/* Verdict skeleton */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-6 w-16 bg-gray-200 rounded-full" />
            <div className="h-4 w-48 bg-gray-200 rounded" />
          </div>
        </div>
        
        {/* Brief skeleton */}
        <div className="rounded-lg border border-gray-200 bg-white p-4 animate-pulse">
          <div className="h-5 w-32 bg-gray-200 rounded mb-3" />
          <div className="space-y-2">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-4 w-3/4 bg-gray-200 rounded" />
            <div className="h-4 w-5/6 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* PIQ 9.0 Task 7: Tightened spacing, visual hierarchy */}
      <div className="space-y-2">
        {/* 1) Verdict Bar (<=72px) */}
        <VerdictBar 
          brainContract={brainContract}
          onWhyClick={handleWhyClick}
        />

        {/* 2) Executive Brief (PRIMARY) */}
        <ExecBriefPrimaryCard
          data={execBriefData}
          isLoading={isExecBriefLoading}
          onGenerateBrief={onGenerateBrief}
          onMetricClick={handleMetricClick}
          showDriversSummary
          brainContract={brainContract}
        />

        {/* 3) Decision Section - PIQ 9.0: Single stack (ACT/PREPARE/HOLD/CLEAR) */}
        <DecisionSection
          brainContract={brainContract}
          posture={posture}
          decisionStack={decisionStack}
          actions={actionableItems}
          containmentPlaybook={containmentPlaybook}
          preventivePlaybook={preventivePlaybook}
          onActionClick={onActionClick}
          onConvertPlaybook={onConvertPlaybook}
          onOpenDetail={handleMetricClick}
        />

        {/* 4) Watchlist Chips (HOLD/CLEAR only) - PIQ 9.0: Progressive disclosure */}
        {showWatchlist && (
          <WatchlistChips
            opportunities={operatorView.insightsFeed.opportunities}
            marketing={operatorView.insightsFeed.marketing}
            dataHealth={operatorView.insightsFeed.dataHealth}
            onChipClick={handleChipClick}
          />
        )}

        {/* 5) "View all insights" link */}
        {totalInsights > 0 && (
          <button
            onClick={handleViewAllInsights}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50/50 rounded-lg transition-colors"
          >
            View all insights ({totalInsights})
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Universal Insight Drawer */}
      <InsightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setFilteredChannel(undefined);
        }}
        brainContract={brainContract}
        insights={operatorView.insightsFeed}
        initialTab={drawerTab}
        filteredChannel={filteredChannel}
      />
    </>
  );
}

export default NarrativeSpine;
